import { useEffect, useState } from 'react';
import { LIVE_TV_TRUSTED_CATALOG_MODE } from '../constants/liveTv.constants';
import { getLiveTvChannelById, hasPlayableSource, isTrustedLiveTvChannel } from '../services/liveTv.service';
import { LiveTvChannelWithStream } from '../types/liveTv.types';

interface UseLiveTvChannelResult {
    channel: LiveTvChannelWithStream | null;
    loading: boolean;
    error: string | null;
}

export function useLiveTvChannel(channelId?: string): UseLiveTvChannelResult {
    const [channel, setChannel] = useState<LiveTvChannelWithStream | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!channelId) {
                setChannel(null);
                setLoading(false);
                setError('Missing channel id');
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const data = await getLiveTvChannelById(channelId);

                if (cancelled) return;

                if (!data) {
                    setChannel(null);
                    setError('Channel not found');
                    return;
                }

                if (!data.enabled) {
                    setChannel(null);
                    setError('Channel is temporarily unavailable');
                    return;
                }

                if (!hasPlayableSource(data) && !data.forceEnable) {
                    setChannel(null);
                    setError('Channel source is not available');
                    return;
                }

                if (LIVE_TV_TRUSTED_CATALOG_MODE && !data.forceEnable && !isTrustedLiveTvChannel(data)) {
                    setChannel(null);
                    setError('Channel is not available in the trusted catalog');
                    return;
                }

                setChannel(data);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Unknown error');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, [channelId]);

    return { channel, loading, error };
}
